import { BackgroundVisibility } from "building-types/settings/sheetsTypesEnum";
import type { SheetGeometryCategoryStyle } from "building-types/views/planSets";
import type { SheetGeometryCategoryTable } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { SheetGeometryPropCategories } from "building-types/views/sheetGeometryCategory";
import colors from "common/constants/colorsOld";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import { percentNew } from "common/utils/nominalNumbers";
import { LW_EXTRA_THIN, LW_THIN, PROP_COLOR, WALL_CUT_STROKE_WIDTH } from "doc/plans/constants";
import type { SheetGeometryCategoryStyleSheets } from "doc/plans/SheetGeometrySourceStyleOverrideTable";
import { TAG_FONT_SIZE } from "pdf/planFonts";

function makeStyleOverridesForPropCategories(style: SheetGeometryCategoryStyle): SheetGeometryCategoryStyleSheets {
  const styles: SheetGeometryCategoryTable<SheetGeometryCategoryStyle> = {};

  for (const category of SheetGeometryPropCategories) {
    styles[category] = style;
  }

  return styles;
}

export function makeFloorplanLayerStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return makeStyleOverridesForPropCategories({ line: { strokeColor: PROP_COLOR } });
}

export function makeElectricalPlanLayerStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return {
    ...makeStyleOverridesForPropCategories({ line: { strokeColor: PROP_COLOR } }),
    [SheetGeometryCategory.PERIMS_WALL]: { line: { strokeColor: colors.black, dash: true } },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: { line: { strokeColor: colors.black } },
  };
}

export function makeStructuralLayerStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return {
    ...makeStyleOverridesForPropCategories({ line: { strokeColor: PROP_COLOR } }),
    [SheetGeometryCategory.PERIMS_WALL]: { line: { strokeColor: colors.black } },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: { line: { strokeColor: colors.black } },
  };
}

export function makeFloorPlanMaterialsLayerStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return {
    [SheetGeometryCategory.PERIMS_WALL]: { line: { strokeColor: colors.black, dash: true } },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: { line: { strokeColor: colors.black } },
    [SheetGeometryCategory.WALLS]: { line: { fill: false } },
    [SheetGeometryCategory.WALL_CORE]: { line: { fill: false } },
    ...makeStyleOverridesForPropCategories({ line: { strokeColor: colors.black } }),
    [SheetGeometryCategory.CASEWORK]: { line: { strokeColor: colors.black } },
  };
}

export function makeBrochureFloorPlanSummaryLayerStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return {
    [SheetGeometryCategory.PORCH_COLUMNS]: {
      line: {
        linewidth: LW_EXTRA_THIN,
        strokeColor: colors.stone,
        fillColor: colors.stone,
      },
    },
    [SheetGeometryCategory.PORCH_BEAMS]: { line: { linewidth: LW_EXTRA_THIN, strokeColor: colors.stone } },
    [SheetGeometryCategory.WINDOWS]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.PERIMS_WALL]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.WALLS]: {
      line: {
        linewidth: ZERO_POINTS,
        fillColor: colors.stoneDark,
        strokeColor: colors.stoneDark,
        fillOpacity: percentNew(0.8),
      },
    },
    [SheetGeometryCategory.WALL_CORE]: {
      line: {
        linewidth: ZERO_POINTS,
        fillColor: colors.stoneDark,
        strokeColor: colors.stoneDark,
        fillOpacity: percentNew(0.8),
      },
    },
    [SheetGeometryCategory.DOORS]: { line: { strokeColor: colors.stoneLightX2 } },
    [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: { line: { strokeColor: colors.stoneLightX2 } },
    [SheetGeometryCategory.CHASE_ROOMS]: { line: { linewidth: ZERO_POINTS, strokeColor: colors.stoneDark } },
    ...makeStyleOverridesForPropCategories({ line: { strokeColor: colors.stoneLight } }),
    [SheetGeometryCategory.CASEWORK_SOLID_DASH]: { line: { strokeColor: colors.stoneLight } },
    [SheetGeometryCategory.STAIR_ABOVE]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.STAIR_PROJECTED]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.RAILING_PROJECTED]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.RAILING_ABOVE]: { line: { strokeColor: colors.stone } },
    [SheetGeometryCategory.ELECTRICAL_CONTROLLERS]: { line: { strokeColor: colors.stoneDark } },
    [SheetGeometryCategory.ELECTRICAL_LOADS]: { line: { strokeColor: colors.stoneDark } },
    [SheetGeometryCategory.ANNOT_DETAIL]: { line: { strokeColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_LINEWORK]: { line: { strokeColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_NOTE]: { line: { fillColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_DIM_LABEL]: { line: { fillColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_DIM_LINEWORK]: { line: { strokeColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_DIM_WITNESS]: { line: { strokeColor: colors.stoneLight } },
    [SheetGeometryCategory.ANNOT_DIM_TICK]: { line: { strokeColor: colors.stoneLight } },
  };
}

export function makeFloorPlanSummaryLayerStyleOverrides(floorIndex: number = 0): SheetGeometryCategoryStyleSheets {
  return {
    [SheetGeometryCategory.PORCH_COLUMNS]: { line: { linewidth: LW_THIN, fillColor: colors.black } },
    [SheetGeometryCategory.PORCH_BEAMS]: { line: { linewidth: LW_EXTRA_THIN } },
    [SheetGeometryCategory.WINDOWS]: { line: { strokeColor: colors.stoneDark } },
    [SheetGeometryCategory.PERIMS_WALL]: {
      line: {
        strokeColor: colors.black,
        dash: floorIndex !== 0,
      },
    },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: {
      line: {
        strokeColor: colors.black,
      },
    },
    [SheetGeometryCategory.WALLS]: { line: { fill: false } },
    [SheetGeometryCategory.WALL_CORE]: { line: { fill: false } },
  };
}

export function makeFoundationUtilityPlanStyleOverrides(): SheetGeometryCategoryStyleSheets {
  return {
    [SheetGeometryCategory.SLAB_BELOW]: { line: { dash: true } },
    ...makeStyleOverridesForPropCategories({ line: { strokeColor: colors.stone, linewidth: LW_THIN } }),
    [SheetGeometryCategory.ANNOT_NOTE]: { text: { fontSize: TAG_FONT_SIZE } },
  };
}

export function makeBackgroundPlanStyleOverrides(
  backgroundVisibility = BackgroundVisibility.BACKGROUND,
): SheetGeometryCategoryStyleSheets {
  const fullBackgroundVisibility = backgroundVisibility === BackgroundVisibility.FULL;
  const porchLineStyle = fullBackgroundVisibility ? undefined : { strokeColor: colors.stone };

  return {
    [SheetGeometryCategory.PORCH_COLUMNS]: { line: porchLineStyle },
    [SheetGeometryCategory.PORCH_BEAMS]: { line: { ...porchLineStyle, dash: false } },
    [SheetGeometryCategory.PORCH]: { line: porchLineStyle },
    [SheetGeometryCategory.DOORS]: fullBackgroundVisibility
      ? undefined
      : { line: { strokeColor: colors.stoneLightX2 } },
    [SheetGeometryCategory.DOOR_OPERATION_LINEWORK]: fullBackgroundVisibility
      ? undefined
      : { line: { strokeColor: colors.stoneLightX2 } },
    [SheetGeometryCategory.WINDOWS]: {
      line: { strokeColor: fullBackgroundVisibility ? colors.black : colors.stoneLightX2 },
    },
    [SheetGeometryCategory.PERIMS_WALL]: {
      line: { strokeColor: fullBackgroundVisibility ? colors.black : colors.stoneLightX2 },
    },
    [SheetGeometryCategory.FLOOR_HEIGHT_DIFFERENCE]: {
      line: { strokeColor: fullBackgroundVisibility ? colors.black : colors.stoneLightX2 },
    },
    [SheetGeometryCategory.WALL_CORE]: {
      line: fullBackgroundVisibility
        ? undefined
        : {
            linewidth: LW_EXTRA_THIN,
            fillColor: colors.stoneLightX3,
            strokeColor: colors.stoneDark,
          },
    },
    [SheetGeometryCategory.CHASE_ROOMS]: {
      line: {
        linewidth: fullBackgroundVisibility ? WALL_CUT_STROKE_WIDTH : LW_EXTRA_THIN,
        strokeColor: fullBackgroundVisibility ? undefined : colors.stoneDark,
      },
    },
    ...makeStyleOverridesForPropCategories({
      line: { strokeColor: fullBackgroundVisibility ? PROP_COLOR : colors.stoneDark },
    }),
  };
}
